// ./src/contexts/VehicleTasksContext.tsx
//
// Context wrapper around the useVehicleTasks hook.
//
// Purpose:
// - Allows sibling components (list, edit drawer, toolbar)
//   to share the same vehicle tasks state without prop-drilling.
// - Each page creates its own <VehicleTasksProvider> with its own config,
//   so VehicleTasksPage filters don't affect DashboardPage, etc.
// - The underlying useVehicleTasks hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In a page component:
//   <VehicleTasksProvider autoLoad={false} pageSize={50}>
//     <VehicleTasksPageContent />
//     <VehicleTaskEditDrawer />
//   </VehicleTasksProvider>
//
//   // In any child component:
//   const { tasks, isLoading, create, ... } = useVehicleTasksContext();
//
//   // In a component that may or may not be inside a provider (e.g. drawer):
//   const context = useOptionalVehicleTasksContext();
//   const createFn = context?.create ?? props.onCreate;

import React, { createContext, useContext } from "react";

import { useVehicleTasks, UseVehicleTasksOptions, UseVehicleTasksReturn } from "@/hooks/useVehicleTasks";

// ============================================
// Context
// ============================================

const VehicleTasksContext = createContext<UseVehicleTasksReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface VehicleTasksProviderProps extends UseVehicleTasksOptions {
  children: React.ReactNode;
}

/**
 * Wraps useVehicleTasks into a context provider.
 *
 * Each instance creates its own independent useVehicleTasks store
 * with its own filters, pagination, loading state, and error state.
 *
 * @example
 * <VehicleTasksProvider autoLoad={false} pageSize={50} initialSortBy={[{ name: "dueDate", order: "ASC" }]}>
 *   <MyPageContent />
 * </VehicleTasksProvider>
 */
export const VehicleTasksProvider: React.FC<VehicleTasksProviderProps> = ({
  children,
  ...options
}) => {
  const vehicleTasks = useVehicleTasks(options);

  return (
    <VehicleTasksContext.Provider value={vehicleTasks}>
      {children}
    </VehicleTasksContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the VehicleTasksContext. Throws if used outside a VehicleTasksProvider.
 *
 * Use this in components that are always rendered inside a provider
 * (e.g. VehicleTaskList, toolbar components).
 */
export const useVehicleTasksContext = (): UseVehicleTasksReturn => {
  const context = useContext(VehicleTasksContext);

  if (!context) {
    throw new Error(
      "useVehicleTasksContext must be used within a <VehicleTasksProvider>. " +
      "Wrap your component tree with <VehicleTasksProvider> or use useOptionalVehicleTasksContext() instead."
    );
  }

  return context;
};

/**
 * Access the VehicleTasksContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider
 * (e.g. VehicleTaskEditDrawer which works both inside VehicleTasksPage's provider
 * and standalone on DashboardPage).
 *
 * @example
 * const context = useOptionalVehicleTasksContext();
 * const createFn = context?.create ?? props.onCreate;
 * const loadFn = context?.get ?? props.onLoad;
 */
export const useOptionalVehicleTasksContext = (): UseVehicleTasksReturn | null => {
  return useContext(VehicleTasksContext);
};